/**
 * Developed by Spell Solutions for Trivinci Systems, LLC.
 *
 * Developer attribution only. Ownership, licensing, and permitted use are
 * governed by the applicable agreement and applicable law.
 */
import { LitElement, css, html, unsafeCSS } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import L from "leaflet";
// Inline Leaflet's stylesheet so it lives inside this component's Shadow DOM
// — external <link rel="stylesheet"> rules never cross the shadow boundary,
// and without `.leaflet-container { position: relative }` etc. tiles can only
// render in a tiny strip at the top of the host.
import leafletCss from "leaflet/dist/leaflet.css?inline";
import type { TelemetryLatest } from "../services/telemetry";
import { TELEMETRY_STALE_MS } from "../services/config";
import { RK_VERSION } from "../lib/version";
import { ReverseGeocoder, formatCoords } from "../services/geocode";
import "./rk-hud";
import type { RkHud } from "./rk-hud";

/**
 * <rk-panel logger-id="..." open hud-dock="video|panel">
 *
 * Mirrors v2's RightPanel layout faithfully:
 *  - Header:        title + Live/Offline pill + Open in Maps + Close
 *  - Map card:      header (title + GPS sub + Map/Satellite toggle) + padded host + Leaflet
 *  - HUD section:   "Show HUD in this panel" toggle; when on, hosts an <rk-hud variant="panel">
 *
 * Map init is deferred until ResizeObserver reports a non-zero container,
 * because Leaflet computes its tile pyramid against the initial container
 * size and `invalidateSize` later does NOT repopulate the tile cache for
 * newly-revealed area. Initialising too early leaves the bottom of the
 * map host empty.
 *
 * Events:
 *  - "toggle"   user clicked open/close
 *  - "huddock"  user toggled HUD dock; detail: "video" | "panel"
 */

type MapMode = "default" | "satellite";

const TRAIL_MAX = 5000;
const TRAIL_MIN_SEG_M = 3;
// Draw-once trail: the red line draws the route as the car completes its FIRST lap,
// then FREEZES as a fixed outline. On every lap after, the line is left exactly as
// it is (never redrawn, never a second layer), and only the car marker moves along
// it. Lap completion = after at least TRAIL_MIN_LOOP_M of path, the latest movement
// segment passes within TRAIL_CLOSE_R_M of where the trail started (the MIN_LOOP
// guard keeps hairpins/chicanes from counting as a full lap).
const TRAIL_CLOSE_R_M = 20;
const TRAIL_MIN_LOOP_M = 120;
/** A closed loop must also enclose this much ground before it counts as a lap.
 *  Path length alone cannot tell a lap from a there-and-back along the same
 *  strip of tarmac: rolling out of a pit box and turning round covers the 120 m
 *  and passes back through the start, which closed the lap and froze the route
 *  as a short stub before the car ever reached the circuit. A there-and-back
 *  encloses no ground at all, while the tightest kart circuit still encloses
 *  about 15,000 m2, so this separates them with room to spare. */
const TRAIL_MIN_LOOP_AREA_M2 = 5000;
/* GPS spike rejection. The trail had a minimum segment length but no maximum, so a
   single bad fix was drawn as a straight line clean across the map — the scribble
   people report as a "bad map". Anything implying a speed no race car reaches is
   treated as a bad fix and kept out of the line. A genuine relocation (signal lost
   and reacquired somewhere else) keeps failing this test, so after a few in a row we
   accept it and start the line again from there rather than stay stuck. */
const TRAIL_MAX_IMPLIED_MPH = 250;
const TRAIL_SPIKES_BEFORE_RELOCATE = 3;

/** Distance (m) from point q to segment [a,b], via a local flat-earth projection
 *  (fine at these tens-of-metres scales). Used for lap-closure detection so a
 *  coarse GPS fix at race speed can't jump over the closure radius. */
function distToSegment(q: [number, number], a: [number, number], b: [number, number]): number {
  const mLat = 111320, mLon = 111320 * Math.cos((a[0] * Math.PI) / 180);
  const bx = (b[1] - a[1]) * mLon, by = (b[0] - a[0]) * mLat;
  const qx = (q[1] - a[1]) * mLon, qy = (q[0] - a[0]) * mLat;
  const len2 = bx * bx + by * by;
  let t = len2 > 0 ? (qx * bx + qy * by) / len2 : 0;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(qx - t * bx, qy - t * by);
}
const TRAIL_KEY = (id: string) => `rk-trail:${id}`;
// Restore a saved trail only if it was written within this window, so a refresh
// or a backgrounded/discarded tab keeps its trail, but an old session from hours
// ago starts fresh rather than showing a stale lap.
const TRAIL_TTL_MS = 20 * 60 * 1000;

/* ---- Motion tuning (mirrors v2) ----
   GPS arrives every ~200 ms; we tween the marker over up to 1.8 s so it
   moves smoothly even when samples skip. Distance drives duration so a
   200 m hop doesn't take the same time as a 2 m crawl. */
// Marker catch-up tween. Kept SHORT so the dot rides the leading edge of the
// trail instead of lagging it: in-band telemetry is frame-locked, so the latest
// fix is "where the car is" — a long ease (the old 950–1800ms) trailed behind
// fast motion and cut inside curves. Sub-meter parked jitter is filtered
// separately (LOW_SPEED_JITTER_M), so a tight tween doesn't make a still car
// twitch.
// Marker follow: critically-damped exponential smoothing toward the latest
// (interpolated, frame-locked) target at display refresh — a continuous low-pass
// with no per-update velocity discontinuity, so the dot glides without the
// wobble a re-targeting tween produced. TAU = smoothing time constant (ms);
// lower = snappier/less lag, higher = smoother.
const MARKER_FOLLOW_TAU_MS = 70;
// Beyond this, snap instead of gliding (reconnect / first fix / telemetry gap)
// so the dot doesn't streak across the map.
const MARKER_SNAP_M = 80;
const LOW_SPEED_LOCK_MPH = 3;
const LOW_SPEED_JITTER_M = 2.5;
const MOVING_JITTER_M = 0.8;

/** Ground enclosed by the route if its ends were joined, in square metres.
 *  Shoelace over a local metre plane anchored at the first point, which is
 *  accurate enough at circuit scale and needs no projection. */
function enclosedAreaM2(pts: [number, number][]): number {
  if (pts.length < 3) return 0;
  const o = pts[0];
  const mLat = 111320, mLon = 111320 * Math.cos((o[0] * Math.PI) / 180);
  let sum = 0;
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i], q = pts[(i + 1) % pts.length];
    sum += ((p[1] - o[1]) * mLon) * ((q[0] - o[0]) * mLat)
         - ((q[1] - o[1]) * mLon) * ((p[0] - o[0]) * mLat);
  }
  return Math.abs(sum) / 2;
}
function distanceMeters(a: [number, number], b: [number, number]): number {
  const R = 6_371_000;
  const dLat = ((b[0] - a[0]) * Math.PI) / 180;
  const dLon = ((b[1] - a[1]) * Math.PI) / 180;
  const lat1 = (a[0] * Math.PI) / 180;
  const lat2 = (b[0] * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
function lerpPoint(
  a: [number, number],
  b: [number, number],
  t: number
): [number, number] {
  return [lerp(a[0], b[0], t), lerp(a[1], b[1], t)];
}
function easeOutCubic(v: number): number {
  return v < 0.5 ? 4 * v * v * v : 1 - Math.pow(-2 * v + 2, 3) / 2;
}
function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

/** Position marker — a simple dot (per client request: no car blob).
 *  Kept as a divIcon with the same `rk-car-marker` class so the existing
 *  staleness styling + tween machinery still apply. */
function buildCarIcon(): L.DivIcon {
  const svg = `
    <svg width="22" height="22" viewBox="0 0 22 22" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="rk-dot-shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="1" stdDeviation="1.5" flood-color="rgba(0,0,0,0.6)"/>
        </filter>
      </defs>
      <circle cx="11" cy="11" r="10" fill="rgba(56,189,248,0.22)"/>
      <circle cx="11" cy="11" r="5.5" fill="rgba(56,189,248,1)"
              stroke="#ffffff" stroke-width="2" filter="url(#rk-dot-shadow)"/>
    </svg>`;
  return L.divIcon({
    className: "rk-car-marker",
    html: svg,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
}

@customElement("rk-panel")
export class RkPanel extends LitElement {
  static styles = [
    unsafeCSS(leafletCss),
    css`
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
      width: 46px;
      background: rgba(15, 23, 36, 0.72);
      backdrop-filter: blur(10px);
      border-left: 1px solid var(--rk-border);
      transition: width var(--rk-dur-slow) var(--rk-ease-out);
      overflow: hidden;
      position: relative;
      min-height: 0;
    }
    :host([open]) {
      width: min(380px, 44vw);
    }

    /* Collapsed tab */
    .tab {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      display: grid;
      place-items: center;
      background: transparent;
      border: 0;
      cursor: pointer;
      color: var(--rk-text-muted);
      transition: color var(--rk-dur-fast);
      z-index: 5;
    }
    :host([open]) .tab { display: none; }
    .tab:hover { color: var(--rk-text); }
    .tab-label {
      writing-mode: vertical-rl;
      transform: rotate(180deg);
      letter-spacing: 0.35px;
      font-weight: 650;
      color: rgba(255, 255, 255, 0.72);
      font-size: 12px;
    }

    .inner {
      flex: 1;
      display: flex;
      flex-direction: column;
      min-height: 0;
      opacity: 0;
      transition: opacity var(--rk-dur-slow) var(--rk-ease-out);
      pointer-events: none;
    }
    :host([open]) .inner { opacity: 1; pointer-events: auto; }

    /* ---- Header ---- */
    .head {
      flex: 0 0 auto;
      padding: 14px 14px 10px;
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 12px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    }
    .head-title {
      font-size: 15px;
      font-weight: 800;
      letter-spacing: -0.01em;
    }
    .head-sub {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-top: 6px;
      font-size: 12px;
      color: rgba(255, 255, 255, 0.6);
    }
    .pill {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 2px 8px;
      border-radius: var(--rk-r-pill);
      font-family: var(--rk-font-mono);
      font-size: 10px;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      background: rgba(34, 211, 238, 0.12);
      color: var(--rk-live);
      border: 1px solid rgba(34, 211, 238, 0.32);
    }
    .pill[data-stale="true"] {
      background: rgba(251, 113, 133, 0.12);
      color: var(--rk-bad);
      border-color: rgba(251, 113, 133, 0.32);
    }
    .pill .dot {
      width: 6px; height: 6px; border-radius: 999px;
      background: currentColor; box-shadow: 0 0 6px currentColor;
    }
    .head-actions { display: flex; gap: 6px; align-items: center; }
    .btn {
      font-size: 12px;
      font-weight: 600;
      color: var(--rk-text-muted);
      background: rgba(255, 255, 255, 0.04);
      border: 1px solid var(--rk-border);
      padding: 6px 10px;
      border-radius: 10px;
      cursor: pointer;
      transition: background var(--rk-dur-fast),
        color var(--rk-dur-fast), border-color var(--rk-dur-fast);
    }
    .btn:hover {
      color: var(--rk-text);
      background: rgba(255, 255, 255, 0.08);
      border-color: var(--rk-border-strong);
    }
    .btn[aria-pressed="true"] {
      background: rgba(56, 189, 248, 0.22);
      color: var(--rk-brand-primary);
      border-color: var(--rk-border-brand);
    }
    a.btn { text-decoration: none; display: inline-block; }
    .btn[disabled] { opacity: 0.4; pointer-events: none; }

    /* ---- Scrollable content area ---- */
    .content {
      flex: 1 1 auto;
      min-height: 0;
      overflow: auto;
      -webkit-overflow-scrolling: touch;
      padding: 14px;
      display: grid;
      gap: 12px;
    }
    .content::-webkit-scrollbar { width: 6px; }
    .content::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.12);
      border-radius: 6px;
    }

    /* ---- Map card (matches v2 .rk-mapCard) ---- */
    .map-card {
      border: 1px solid rgba(255, 255, 255, 0.10);
      border-radius: 14px;
      background: rgba(255, 255, 255, 0.05);
      overflow: hidden;
    }
    .map-head {
      padding: 10px 12px 8px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 8px;
    }
    .map-head-text { min-width: 0; flex: 1; }
    .map-title {
      font-weight: 750;
      letter-spacing: 0.2px;
      font-size: 12px;
    }
    .map-sub {
      margin-top: 4px;
      font-size: 12px;
      color: var(--rk-text-muted);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .map-mode { display: flex; gap: 6px; flex-shrink: 0; }
    .map-mode .btn { padding: 4px 8px; font-size: 11px; border-radius: 8px; }

    .map-host {
      padding: 8px 8px 10px;
    }
    .map-leaflet {
      position: relative;
      width: 100%;
      height: clamp(190px, 32vh, 300px);
      border-radius: 12px;
      overflow: hidden;
      background: rgba(0, 0, 0, 0.25);
      border: 1px solid rgba(255, 255, 255, 0.10);
    }
    .map-empty {
      position: absolute;
      inset: 0;
      display: grid;
      place-items: center;
      pointer-events: none;
      color: var(--rk-text-muted);
      font-size: 12px;
      background: rgba(6, 8, 16, 0.55);
      opacity: 0;
      transition: opacity var(--rk-dur-base);
      z-index: 600;
    }
    .map-empty[show] { opacity: 1; }

    /* Strip the default Leaflet white background from our divIcon car. */
    :host .rk-car-marker {
      background: transparent !important;
      border: 0 !important;
      transition: opacity 240ms ease, filter 240ms ease;
    }
    /* When the displayed GPS is stale (server says so, or sourceTimestampMs
       is older than the threshold), fade the icon and tint it rose so it is
       obvious the position is not current. */
    :host .rk-car-marker.stale {
      opacity: 0.4;
      filter: grayscale(70%) hue-rotate(300deg);
    }

    /* Top-right age badge over the map, shown only when stale. */
    .map-stale-badge {
      position: absolute;
      top: 8px;
      right: 8px;
      z-index: 500;
      padding: 3px 9px;
      border-radius: 999px;
      font-family: var(--rk-font-mono);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      background: rgba(251, 113, 133, 0.85);
      color: #fff;
      border: 1px solid rgba(255, 255, 255, 0.4);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
      pointer-events: none;
      display: none;
    }
    .map-host .map-stale-badge[show] { display: inline-flex; align-items: center; gap: 5px; }
    .map-stale-badge .dot {
      width: 6px; height: 6px; border-radius: 999px; background: #fff;
      animation: fresh-pulse 1.4s ease-in-out infinite;
    }
    @keyframes fresh-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }

    /* ---- HUD section ---- */
    .section {
      display: flex;
      flex-direction: column;
      gap: 10px;
      padding: 12px;
      border-radius: 14px;
      border: 1px solid var(--rk-border);
      background: rgba(255, 255, 255, 0.04);
    }
    .section-title { font-weight: 750; font-size: 12px; letter-spacing: 0.2px; }
    .toggle-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
    }
    .toggle-text { font-size: 13px; color: var(--rk-text); }
    .switch {
      position: relative;
      width: 42px;
      height: 22px;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid var(--rk-border);
      cursor: pointer;
      transition: background var(--rk-dur-base);
      flex-shrink: 0;
    }
    .switch[on] {
      background: rgba(56, 189, 248, 0.55);
      border-color: var(--rk-border-brand);
    }
    .switch-thumb {
      position: absolute;
      top: 1px;
      left: 1px;
      width: 18px;
      height: 18px;
      border-radius: 999px;
      background: #fff;
      transition: transform var(--rk-dur-base) var(--rk-ease-spring);
    }
    .switch[on] .switch-thumb { transform: translateX(20px); }
    .section-hint {
      font-size: 11px;
      color: var(--rk-text-dim);
      line-height: 1.5;
    }
    .panel-hud { margin-top: 4px; }
    /* Dimmer than .section-hint and monospaced, so it reads as a build tag
       rather than as copy. Selectable, because the first thing anyone does
       with it is paste it into a message. */
    .build-stamp {
      margin-top: auto;
      padding-top: 10px;
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      font-size: 10px;
      letter-spacing: 0.02em;
      color: var(--rk-text-dim);
      opacity: 0.55;
      text-align: right;
      user-select: text;
    }

    /* Slot that hosts the 4 video controls (Swap/PiP/Mute/Fullscreen)
       when the HUD is docked into this panel. Sits at the very bottom
       of the panel, horizontal flex row, divider above. */
    .panel-foot {
      flex: 0 0 auto;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 10px 12px;
      border-top: 1px solid var(--rk-border);
      background: rgba(8, 11, 18, 0.35);
    }
    .panel-foot ::slotted(button) {
      width: 40px;
      height: 40px;
    }

    /* Tight spacing when HUD is docked here */
    :host([hud-dock="panel"]) .content {
      padding: 10px;
      gap: 8px;
    }
    :host([hud-dock="panel"]) .map-leaflet {
      height: clamp(190px, 32vh, 280px);
    }

    @media (max-width: 740px) {
      :host([open]) { width: min(360px, 60vw); }
      .map-leaflet { height: clamp(170px, 30vh, 240px); }
    }

    /* Hide drawer entirely on small screens */
    @media (max-width: 720px) {
      :host { display: none; }
    }
  `,
  ];

  @property({ attribute: "logger-id" }) loggerId = "";
  @property({ type: Boolean, reflect: true }) open = true;
  @property({ attribute: "hud-dock", reflect: true }) hudDock: "video" | "panel" = "video";
  /** When true, telemetry source is the WebSocket live-push channel
   *  (engineer mode — sub-second updates, decoupled from HLS video).
   *  When false, telemetry is read from EXT-X-DATERANGE tags in HLS
   *  (default — synced with what the viewer sees). */
  @property({ attribute: "live-mode", type: Boolean, reflect: true }) liveMode = false;

  @state() private lat: number | null = null;
  @state() private lon: number | null = null;
  @state() private stale = true;
  // Pill liveness: video current OR telemetry fresh (telemetry alone drives
  // `stale`, which still governs the map marker / telemetry values).
  @state() private streamLive = false;
  @state() private mapMode: MapMode = "satellite";

  @query("#map") private mapDiv!: HTMLDivElement;
  @query(".map-stale-badge") private staleBadgeEl!: HTMLDivElement;
  @query("rk-hud") private panelHud?: RkHud;

  private map: L.Map | null = null;
  private marker: L.Marker | null = null;
  private trail: L.Polyline | null = null;
  private geocoder = new ReverseGeocoder(() => this.requestUpdate());
  private baseLayer: L.TileLayer | null = null;
  private labelsLayer: L.TileLayer | null = null;
  private trailPoints: [number, number][] = [];
  private lapComplete = false;
  private lastPersist = 0;
  /** When the last point was accepted into the trail, for the spike test. */
  private lastTrailAt = 0;
  /** Consecutive rejected fixes, so a real relocation is not blocked forever. */
  private spikeRun = 0;
  private resizeObs: ResizeObserver | null = null;
  /** Pending position to display once the map initialises. */
  private pendingPosition: [number, number] | null = null;

  /** Marker tween state — what's currently rendered + where we're heading. */
  private renderedPos: [number, number] | null = null;
  /** Smoothed telemetry point (jitter-filtered) → input to the tween. */
  private smoothedPos: [number, number] | null = null;
  /** Latest target the marker eases toward (the exponential-follow setpoint). */
  private markerTarget: [number, number] | null = null;
  private tweenRaf: number | null = null;

  /** Cache last snapshot to feed a panel HUD that mounts after we got data. */
  private lastSnap: TelemetryLatest | null = null;
  private lastHudSnap: TelemetryLatest | null = null;
  private lastHudNow = 0;
  private lastNow = 0;

  protected firstUpdated(): void {
    this.loadTrail();
    if (this.open) this.tryInitMap();
  }

  protected updated(changed: Map<string, unknown>): void {
    if (changed.has("loggerId")) {
      this.trailPoints = [];
      this.lapComplete = false;
      this.trail?.setLatLngs([]);
      this.marker?.remove();
      this.marker = null;
      this.lat = null;
      this.lon = null;
      this.loadTrail();
    }
    if (changed.has("open") && this.open && !this.map) {
      this.tryInitMap();
    }
    if (changed.has("mapMode") && this.map) {
      this.applyTileLayer();
    }
    if (changed.has("hudDock")) {
      // Section size changed — refresh map size after layout settles.
      window.setTimeout(() => this.invalidate(), 250);
      if (this.hudDock === "panel" && this.lastHudSnap) {
        queueMicrotask(() => this.panelHud?.feed(this.lastHudSnap!, this.lastHudNow));
      }
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.resizeObs?.disconnect();
    this.resizeObs = null;
    if (this.tweenRaf != null) {
      cancelAnimationFrame(this.tweenRaf);
      this.tweenRaf = null;
    }
    try {
      this.map?.remove();
    } catch {
      /* noop */
    }
    this.map = null;
  }

  /** Push a telemetry snapshot. */
  feed(snap: TelemetryLatest, videoLive = false): void {
    this.lastSnap = snap;
    this.lastNow = Date.now();
    // Match the HUD's three-layer staleness so map and HUD agree:
    //   1. fetch failing (no fresh receivedAtMs)
    //   2. server-side: lastSeenAgeS > staleThresholdS
    //   3. source timestamp older than the threshold (covers hlsMetadata)
    const fetchStale =
      !snap.ok || this.lastNow - snap.receivedAtMs > TELEMETRY_STALE_MS;
    const serverStale =
      snap.lastSeenAgeS != null &&
      snap.staleThresholdS != null &&
      snap.lastSeenAgeS > snap.staleThresholdS;
    this.stale = fetchStale || serverStale;
    // Pill reflects the VIDEO only (its frame PDT freshness). Telemetry staleness
    // must NOT drive it — video and telemetry are separate pipelines, so fresh
    // telemetry with dead video (or vice-versa) should never read "Live". Telemetry
    // staleness still styles the map marker / values via `this.stale`.
    this.streamLive = videoLive;
    this.applyMarkerStaleness();

    if (
      snap.gpsLat != null &&
      snap.gpsLon != null &&
      Number.isFinite(snap.gpsLat) &&
      Number.isFinite(snap.gpsLon)
    ) {
      this.lat = snap.gpsLat;
      this.lon = snap.gpsLon;
      // Jitter-filter the raw GPS sample so a parked car doesn't ant-trail
      // around its actual position. When moving above LOW_SPEED_LOCK_MPH we
      // accept the new point quickly; idle, we blend slowly.
      const raw: [number, number] = [snap.gpsLat, snap.gpsLon];
      const stabilized = this.stabilize(raw, snap.speedMph);
      if (!this.map) {
        this.pendingPosition = stabilized;
      } else {
        this.applyPosition(stabilized);
      }
    }

  }

  /** Push the gauges' snapshot (frame-locked) for the docked HUD. Kept apart
   *  from feed(), which drives the map and may run on the Team feed. */
  feedHud(snap: TelemetryLatest): void {
    this.lastHudSnap = snap;
    this.lastHudNow = Date.now();
    if (this.hudDock === "panel" && this.panelHud) {
      this.panelHud.feed(snap, this.lastHudNow);
    }
  }

  /** Smooth raw GPS to suppress sub-meter jitter when stationary. */
  private stabilize(
    raw: [number, number],
    speedMph: number | null
  ): [number, number] {
    if (!this.smoothedPos) {
      this.smoothedPos = raw;
      return raw;
    }
    const moving = speedMph != null && speedMph >= LOW_SPEED_LOCK_MPH;
    const threshold = moving ? MOVING_JITTER_M : LOW_SPEED_JITTER_M;
    const blend = moving ? 0.48 : 0.12;
    const moved = distanceMeters(this.smoothedPos, raw);
    const next: [number, number] =
      moved <= threshold ? lerpPoint(this.smoothedPos, raw, blend) : raw;
    this.smoothedPos = next;
    return next;
  }

  /* ---- Leaflet plumbing ---- */

  /**
   * Try to initialise the map. If the container has zero dimensions (the
   * panel is mid-transition or hidden), schedule a retry via ResizeObserver
   * and bail. Leaflet must NOT be attached to a zero-size element — its
   * tile-pyramid math caches against the initial size, and later
   * invalidateSize won't repopulate the tile cache for newly-revealed area.
   */
  private tryInitMap(): void {
    if (this.map || !this.mapDiv) return;
    const ready = this.mapDiv.clientWidth >= 40 && this.mapDiv.clientHeight >= 40;
    if (!ready) {
      this.observeUntilReady();
      return;
    }
    this.initMap();
  }

  private observeUntilReady(): void {
    if (this.resizeObs || !this.mapDiv) return;
    this.resizeObs = new ResizeObserver(() => {
      if (!this.mapDiv) return;
      if (this.mapDiv.clientWidth >= 40 && this.mapDiv.clientHeight >= 40) {
        // Disconnect — we only need this until the map exists.
        this.resizeObs?.disconnect();
        this.resizeObs = null;
        this.initMap();
        // After init, re-observe for ongoing resizes so we invalidate on
        // panel/section changes.
        this.observeOngoing();
      }
    });
    this.resizeObs.observe(this.mapDiv);
  }

  private observeOngoing(): void {
    if (!("ResizeObserver" in window) || !this.mapDiv) return;
    const obs = new ResizeObserver(() => this.invalidate());
    obs.observe(this.mapDiv);
    this.resizeObs = obs;
  }

  private initMap(): void {
    if (!this.mapDiv || this.map) return;
    this.map = L.map(this.mapDiv, {
      zoomControl: true,
      attributionControl: false,
      preferCanvas: true,
      zoomSnap: 0.5,
    }).setView([39.5, -98.35], 4);

    this.applyTileLayer();

    this.trail = L.polyline(this.trailPoints, {
      color: "#ef4444",
      weight: 4,
      opacity: 0.92,
      lineCap: "round",
      lineJoin: "round",
    }).addTo(this.map);

    if (this.pendingPosition) {
      this.applyPosition(this.pendingPosition);
      this.pendingPosition = null;
    } else if (this.trailPoints.length) {
      const last = this.trailPoints[this.trailPoints.length - 1];
      this.applyPosition(last);
    }

    // One last invalidate kick to be sure tiles fill the host.
    window.setTimeout(() => this.invalidate(), 80);
    window.setTimeout(() => this.invalidate(), 400);
  }

  private invalidate(): void {
    if (!this.map) return;
    try {
      this.map.invalidateSize({ animate: false, pan: false });
    } catch {
      /* noop */
    }
  }

  /** Visually degrade the car icon when the GPS is stale, plus surface an
   *  age badge on the map. Otherwise the operator sees a stale position
   *  rendered with full confidence — the bug Bill-Stevens-demo hit. */
  private applyMarkerStaleness(): void {
    const el = this.marker?.getElement?.();
    if (el) el.classList.toggle("stale", this.stale);
    if (this.staleBadgeEl) {
      if (this.stale && this.lastSnap?.lastSeenAgeS != null) {
        this.staleBadgeEl.toggleAttribute("show", true);
        const ageS = this.lastSnap.lastSeenAgeS;
        this.staleBadgeEl.querySelector(".age-text")!.textContent =
          ageS < 60 ? `${Math.round(ageS)}s ago` :
          ageS < 3600 ? `${Math.round(ageS / 60)}m ago` :
          ageS < 86400 ? `${Math.round(ageS / 3600)}h ago` :
          `${Math.round(ageS / 86400)}d ago`;
      } else if (this.stale) {
        this.staleBadgeEl.toggleAttribute("show", true);
        this.staleBadgeEl.querySelector(".age-text")!.textContent = "offline";
      } else {
        this.staleBadgeEl.toggleAttribute("show", false);
      }
    }
  }

  private applyTileLayer(): void {
    if (!this.map) return;
    this.baseLayer?.remove();
    this.labelsLayer?.remove();
    if (this.mapMode === "satellite") {
      this.baseLayer = L.tileLayer(
        "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
        { maxZoom: 20 }
      );
      this.labelsLayer = L.tileLayer(
        "https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
        { maxZoom: 20, opacity: 0.95 }
      );
    } else {
      this.baseLayer = L.tileLayer(
        "https://{s}.basemaps.cartocdn.com/rastertiles/voyager_nolabels/{z}/{x}/{y}{r}.png",
        { subdomains: "abcd", maxZoom: 20 }
      );
      this.labelsLayer = L.tileLayer(
        "https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png",
        { subdomains: "abcd", maxZoom: 20, opacity: 1 }
      );
    }
    this.baseLayer.addTo(this.map);
    this.labelsLayer.addTo(this.map);
  }

  private applyPosition(pt: [number, number]): void {
    if (!this.map) {
      this.pendingPosition = pt;
      return;
    }
    if (!this.marker) {
      this.marker = L.marker(pt, { icon: buildCarIcon(), keyboard: false }).addTo(
        this.map
      );
      this.renderedPos = pt;
      this.map.setView(pt, 16);
      window.setTimeout(() => this.invalidate(), 100);
    }

    // Draw-once trail (jitter-filtered + minimum-segment). Keep drawing the route
    // until the first lap closes, then FREEZE it — stop adding to the line so later
    // laps never redraw over it. The marker (below) keeps moving over the frozen route.
    if (!this.lapComplete) {
      const last = this.trailPoints[this.trailPoints.length - 1];
      const moved = last ? distanceMeters(last, pt) : 0;
      if (!last) {
        this.trailPoints.push(pt);
        this.lastTrailAt = this.lastNow;
        this.spikeRun = 0;
      } else if (moved >= TRAIL_MIN_SEG_M) {
        // Reject a fix that could not physically have happened in the time since
        // the last one. Without this a single bad fix draws a line across the map.
        const dtS = Math.max(0.25, (this.lastNow - this.lastTrailAt) / 1000);
        const impliedMph = (moved / dtS) * 2.23694;
        if (impliedMph > TRAIL_MAX_IMPLIED_MPH && this.spikeRun < TRAIL_SPIKES_BEFORE_RELOCATE) {
          this.spikeRun++;                       // bad fix: leave it out of the line
        } else {
          if (this.spikeRun >= TRAIL_SPIKES_BEFORE_RELOCATE) {
            // Repeatedly far away, so the car really is somewhere else. Start the
            // line again there instead of joining the two places with a straight line.
            this.trailPoints = [];
            this.lapComplete = false;
          }
          this.trailPoints.push(pt);
          this.lastTrailAt = this.lastNow;
          this.spikeRun = 0;
        }
      }
      if (this.isLapComplete()) {
        this.lapComplete = true;
        this.trailPoints.push(this.trailPoints[0]); // close the loop cleanly
      }
      if (this.trailPoints.length > TRAIL_MAX) {
        this.trailPoints.splice(0, this.trailPoints.length - TRAIL_MAX);
      }
      this.paintTrail();
      this.persistTrail(); // throttled; keeps a refresh / backgrounded tab in sync
    }

    // Hand the new point to the continuous exponential-follow loop (smooth,
    // wobble-free); snaps instead of gliding on a large jump.
    this.tweenMarkerTo(pt);

    // Soft camera follow — only re-center if the new point drifts toward
    // the edge of the viewport.
    const bounds = this.map.getBounds().pad(-0.22);
    if (!bounds.contains(pt)) this.map.panTo(pt, { animate: true });
  }

  private tweenMarkerTo(target: [number, number]): void {
    if (!this.marker) return;
    // Teleport (reconnect / first fix / telemetry gap): snap, don't streak.
    if (
      this.renderedPos &&
      distanceMeters(this.renderedPos, target) > MARKER_SNAP_M
    ) {
      this.renderedPos = target;
      this.markerTarget = target;
      this.marker.setLatLng(target);
      return;
    }
    this.markerTarget = target;
    if (this.renderedPos == null) {
      this.renderedPos = target;
      this.marker.setLatLng(target);
    }
    // Ensure the continuous follow loop is running; it stops itself once it
    // converges and is restarted here on the next point.
    if (this.tweenRaf == null) this.startMarkerFollow();
  }

  /** Draw the route up to where the marker actually is, not to the newest fix.
   *
   *  The device reports about once a second, so at racing speed each fix lands
   *  30 to 45 m ahead of the car's drawn position while the marker eases across
   *  that gap. Painting the raw point puts the head of the line in front of the
   *  dot it is meant to be trailing, which reads as the line moving first. The
   *  committed geometry is unchanged; only the head vertex is held back to the
   *  marker, and the two coincide the moment it converges. */
  private paintTrail(): void {
    if (!this.trail) return;
    const pts = this.trailPoints;
    if (!pts.length) {
      this.trail.setLatLngs([]);
      return;
    }
    // A frozen lap is final and the marker runs over it, so draw it in full.
    if (this.lapComplete) {
      this.trail.setLatLngs(pts);
      return;
    }
    const head = this.renderedPos ?? pts[pts.length - 1];
    this.trail.setLatLngs([...pts.slice(0, -1), head]);
  }

  /** Continuously ease the marker toward markerTarget with frame-rate-
   *  independent exponential smoothing (a low-pass). Unlike a per-update tween
   *  it never re-targets discontinuously, so motion glides without wobble. */
  private startMarkerFollow(): void {
    let lastT = performance.now();
    const step = (now: number) => {
      if (!this.marker || !this.markerTarget || !this.renderedPos) {
        this.tweenRaf = null;
        return;
      }
      const dt = Math.max(1, now - lastT);
      lastT = now;
      const a = 1 - Math.exp(-dt / MARKER_FOLLOW_TAU_MS);
      const cur = this.renderedPos;
      const tgt = this.markerTarget;
      const dLat = tgt[0] - cur[0];
      const dLon = tgt[1] - cur[1];
      if (Math.abs(dLat) < 1e-7 && Math.abs(dLon) < 1e-7) {
        this.renderedPos = tgt;
        this.marker.setLatLng(tgt);
        if (!this.lapComplete) this.paintTrail();
        this.tweenRaf = null; // converged — restarts on next applyPosition
        return;
      }
      const nx: [number, number] = [cur[0] + dLat * a, cur[1] + dLon * a];
      this.renderedPos = nx;
      // Keep the head of the line with the dot while it catches up. Skipped once
      // the lap is frozen: that route is final and must not be repainted.
      if (!this.lapComplete) this.paintTrail();
      this.marker.setLatLng(nx);
      this.tweenRaf = requestAnimationFrame(step);
    };
    this.tweenRaf = requestAnimationFrame(step);
  }

  private loadTrail(): void {
    if (!this.loggerId) return;
    try {
      const raw = localStorage.getItem(TRAIL_KEY(this.loggerId));
      if (!raw) return;
      const obj = JSON.parse(raw) as { t?: number; pts?: [number, number][]; done?: boolean };
      if (!obj || !Array.isArray(obj.pts)) return;
      if (typeof obj.t === "number" && Date.now() - obj.t > TRAIL_TTL_MS) return; // stale session
      this.trailPoints = obj.pts.slice(-TRAIL_MAX);
      this.lapComplete = !!obj.done; // a frozen route stays frozen after a refresh
      this.trail?.setLatLngs(this.trailPoints);
    } catch {
      /* noop */
    }
  }
  private persistTrail(): void {
    if (!this.loggerId) return;
    const now = Date.now();
    if (now - this.lastPersist < 3000) return; // throttle writes
    this.lastPersist = now;
    const key = TRAIL_KEY(this.loggerId);
    const done = this.lapComplete;
    const pts = this.trailPoints;
    // Defer the JSON.stringify + localStorage write off the animation frame so it
    // never hitches the marker glide (both are synchronous and grow with the trail).
    const write = () => {
      try { localStorage.setItem(key, JSON.stringify({ t: now, pts, done })); } catch { /* quota */ }
    };
    const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void }).requestIdleCallback;
    if (ric) ric(write, { timeout: 2000 });
    else setTimeout(write, 0);
  }

  /** True once the drawn route has closed its first loop: after at least
   *  TRAIL_MIN_LOOP_M of path, the latest movement SEGMENT passes within
   *  TRAIL_CLOSE_R_M of the lap start, and the route encloses real ground. Uses
   *  the segment (not point-to-point) so a coarse GPS fix at race speed can't
   *  jump clean over the closure radius, and the area so a there-and-back along
   *  one strip of tarmac cannot pass for a lap. */
  private isLapComplete(): boolean {
    const n = this.trailPoints.length;
    if (n < 3) return false;
    let acc = 0;
    for (let i = 0; i < n - 1; i++) acc += distanceMeters(this.trailPoints[i], this.trailPoints[i + 1]);
    if (acc < TRAIL_MIN_LOOP_M) return false;
    if (distToSegment(this.trailPoints[0], this.trailPoints[n - 2], this.trailPoints[n - 1]) > TRAIL_CLOSE_R_M)
      return false;
    return enclosedAreaM2(this.trailPoints) >= TRAIL_MIN_LOOP_AREA_M2;
  }

  /* ---- Event handlers ---- */
  private toggleOpen(): void {
    this.dispatchEvent(new CustomEvent("toggle", { bubbles: true, composed: true }));
  }
  private setMode(m: MapMode): void {
    this.mapMode = m;
  }
  private toggleHudDock(): void {
    const next = this.hudDock === "panel" ? "video" : "panel";
    this.hudDock = next;
    this.dispatchEvent(
      new CustomEvent("huddock", { detail: next, bubbles: true, composed: true })
    );
  }

  /** Clear the drawn route and start again from the car's next position. For when
   *  a stretch of poor GPS has left a line that no longer reflects the track. Also
   *  clears the saved copy so a refresh does not bring the bad line back. */
  private resetTrail(): void {
    this.trailPoints = [];
    this.lapComplete = false;
    this.spikeRun = 0;
    this.lastTrailAt = 0;
    this.trail?.setLatLngs([]);
    if (this.loggerId) {
      try { localStorage.removeItem(TRAIL_KEY(this.loggerId)); } catch { /* noop */ }
    }
    if (this.lat != null && this.lon != null) {
      this.trailPoints = [[this.lat, this.lon]];
      this.lastTrailAt = Date.now();
      this.trail?.setLatLngs(this.trailPoints);
      this.map?.panTo([this.lat, this.lon], { animate: true });
    }
  }

  private toggleLiveMode(): void {
    const next = !this.liveMode;
    this.liveMode = next;
    this.dispatchEvent(
      new CustomEvent("livemode", { detail: next, bubbles: true, composed: true })
    );
  }

  render() {
    const hasFix = this.lat != null && this.lon != null;
    const mapsHref =
      hasFix ? `https://www.google.com/maps?q=${this.lat},${this.lon}` : "#";
    return html`
      <button class="tab" @click=${this.toggleOpen} aria-label="Open details panel">
        <span class="tab-label">Details</span>
      </button>

      <div class="inner">
        <div class="head">
          <div>
            <div class="head-title">Details</div>
            <div class="head-sub">
              <span class="pill" data-stale=${String(!this.streamLive)}>
                <span class="dot"></span>
                ${this.streamLive ? "Live" : "Offline"}
              </span>
            </div>
          </div>
          <div class="head-actions">
            <a
              class="btn"
              href=${mapsHref}
              target="_blank"
              rel="noopener noreferrer"
              ?disabled=${!hasFix}
              >Open in Maps</a
            >
            <button class="btn" @click=${this.toggleOpen}>Close</button>
          </div>
        </div>

        <div class="content">
          <div class="map-card">
            <div class="map-head">
              <div class="map-head-text">
                <div class="map-title">Map</div>
                <div class="map-sub">
                  ${hasFix
                    ? (this.geocoder.label(this.lat, this.lon) ??
                       formatCoords(this.lat!, this.lon!))
                    : "Waiting for GPS…"}
                </div>
              </div>
              <div class="map-mode">
                <button
                  class="btn"
                  aria-pressed=${this.mapMode === "default"}
                  @click=${() => this.setMode("default")}
                >Map</button>
                <button
                  class="btn"
                  aria-pressed=${this.mapMode === "satellite"}
                  @click=${() => this.setMode("satellite")}
                >Satellite</button>
                <button
                  class="btn"
                  title="Team Telemetry"
                  aria-pressed=${this.liveMode}
                  @click=${this.toggleLiveMode}
                >Team</button>
                <button
                  class="btn"
                  title="Clear the drawn route and start again from the car's current position"
                  @click=${this.resetTrail}
                >Reset</button>
              </div>
            </div>
            <div class="map-host">
              <div class="map-leaflet">
                <div id="map" style="position:absolute;inset:0;"></div>
                <div class="map-empty" ?show=${!hasFix}>Waiting for GPS fix…</div>
                <div class="map-stale-badge">
                  <span class="dot"></span>
                  <span>GPS</span>
                  <span class="age-text">offline</span>
                </div>
              </div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">HUD</div>
            <div class="toggle-row">
              <div class="toggle-text">Show HUD in this panel</div>
              <button
                class="switch"
                ?on=${this.hudDock === "panel"}
                @click=${this.toggleHudDock}
                aria-pressed=${this.hudDock === "panel"}
              >
                <span class="switch-thumb"></span>
              </button>
            </div>
            <div class="section-hint">
              When enabled, the HUD is removed from the video and shown here.
            </div>
            ${this.hudDock === "panel"
              ? html`<div class="panel-hud"><rk-hud variant="panel"></rk-hud></div>`
              : ""}
          </div>


          <!-- When HUD is docked here, <rk-player> slots its 4 video
               controls (Swap, PiP, Mute, Fullscreen) into this row so
               they migrate with the HUD instead of stranding on the
               video stage. Empty otherwise. -->
          ${this.hudDock === "panel"
            ? html`<div class="panel-foot"><slot name="panel-controls"></slot></div>`
            : ""}

          <!-- Build stamp. Deliberately the quietest thing on the panel: it is
               for answering "which build are you on?" when a fault is reported,
               not something a viewer needs to read. -->
          <div class="build-stamp" title="Player build">${RK_VERSION}</div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rk-panel": RkPanel;
  }
}
